/**
 * Cards que saíram da Qualificação via fase Venda (transferidos para Comercial).
 * A identificação é pelo histórico: acao=transferido com stage_anterior desta pipeline.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PipelineCard, PipelineStage, StageWithCards } from '../types';

export const pipelineTransferredKey = (pipelineId: string) =>
  ['pipeline-transferred', pipelineId] as const;

const CARD_SELECT = `
  *,
  contratos(
    id,
    valor_bem,
    status,
    grupo_id,
    bem_id,
    cliente_id,
    grupos(numero_grupo, especie, titulo, data_vencimento_grupo),
    bens(codigo_bem, tipo_bem),
    clientes(nome)
  )
`;

export function useTransferredFromPipeline(
  pipelineId: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: pipelineTransferredKey(pipelineId),
    enabled: enabled && !!pipelineId,
    queryFn: () => fetchTransferredFromPipeline(pipelineId),
    staleTime: 0,
  });
}

export async function fetchTransferredFromPipeline(
  pipelineId: string
): Promise<PipelineCard[]> {
  const { data: stages, error: stagesError } = await supabase
    .from('pipeline_stages')
    .select('id')
    .eq('pipeline_id', pipelineId);

  if (stagesError) throw new Error(stagesError.message);

  const stageIds = (stages ?? []).map((s) => s.id);
  if (stageIds.length === 0) return [];

  const { data: history, error: historyError } = await supabase
    .from('card_history')
    .select('card_id, created_at')
    .eq('acao', 'transferido')
    .in('stage_anterior_id', stageIds)
    .order('created_at', { ascending: false });

  if (historyError) throw new Error(historyError.message);

  const cardIds = [
    ...new Set((history ?? []).map((h) => h.card_id).filter(Boolean)),
  ];
  if (cardIds.length === 0) return [];

  const { data: cards, error: cardsError } = await supabase
    .from('pipeline_cards')
    .select(CARD_SELECT)
    .in('id', cardIds)
    .eq('cancelado', false);

  if (cardsError) throw new Error(cardsError.message);

  // Mantém ordem do histórico (mais recentes primeiro)
  const byId = new Map((cards ?? []).map((c) => [c.id, c as PipelineCard]));
  return cardIds.map((id) => byId.get(id)).filter(Boolean) as PipelineCard[];
}

/** Coluna virtual "Venda" para a visão baseada em histórico */
export function buildVendaHistoryStage(cards: PipelineCard[]): StageWithCards {
  const stage: PipelineStage = {
    id: '__venda_historico__',
    pipeline_id: '',
    nome: 'Venda',
    ordem: 0,
    cor: '#22c55e',
    oculta: true,
    visibilidade: 'publico',
  };

  return {
    stage,
    cards,
    count: cards.length,
  };
}
